import {
  API_BASE_URL,
  API_BASE_URL_V2,
  API_SIMULATION_URL,
  API_SIMULATION_URL_V2,
  PAGE_URL,
  isEnterprise,
} from 'config'

import axios from 'axios'
import PropTypes from 'prop-types'

/** Redux and Kea */
import { kea } from 'kea'
import { put, call, cancelled } from 'redux-saga/effects'
import { delay } from 'redux-saga'

import DataLogicV2 from 'containers/AppV2/logic'
import SimpleHabitationLogic from 'containers/SimpleHabitation/logic'
import ResultsSectionV2Logic from 'components/ResultsSectionV2/logic'
import FiltersLogicV2 from 'containers/FiltersSectionV2/logic'

import { csObjectToUnderscore, csObjectFromUnderscore } from 'utils'

/** Contants */
const SIMULATOR_SUGESTIONS_ENDPOINT = `${API_BASE_URL_V2}/public/simulator/simulation_price_list_suggestion/`
const SIMULATOR_GET_SIMULATION = `${API_BASE_URL_V2}/public/simulation/:code/`
const SIMULATION_PROFILE_ENDPOINT = `${API_BASE_URL}/private/simulation-user-profile/`
const ME_ENDPOINT = `${API_BASE_URL}/private/user-profile/me/`
const AUTH_TOKEN_KEY = `tokenauth`

// Simulator url changes depending on the type of the simulator
let SIMULATOR_ENPOINT = `${API_SIMULATION_URL_V2}/public/simulator/simple/`

if (isEnterprise) {
  SIMULATOR_ENPOINT = `${API_SIMULATION_URL}/public/simulator/enterprise/`
}

export default kea({
  path: () => ['scenes', 'resultsV2'],

  connect: {
    actions: [
      DataLogicV2,
      [
        'setValue',
        'setValues',
        'setFinished',
        'setIsUserLoggedIn',
        'iframed',
        'changeSimulationData',
      ],
      FiltersLogicV2,
      [
        'triggerFilter',
        'resetFilters',
        'setFilter',
        'setSearchQuery',
        'resetEntities',
        'setEntitiesCount',
        'setEntitiesOffset',
      ],
      ResultsSectionV2Logic,
      ['setCompareList'],
    ],
    props: [
      DataLogicV2,
      ['values', 'translations', 'finished', 'settings'],
      SimpleHabitationLogic,
      ['currentEquipments'],
      FiltersLogicV2,
      ['filters'],
    ],
  },

  actions: () => ({
    makeSimulation: () => ({}),
    setSimulationResults: (results) => ({ results }),
    setSimulationSugestions: (results) => ({ ...results }),
    changePage: (params) => ({ params }),
    setError: (error) => ({ ...error }),
    saveProfile: (isNewFromUserWithProfiles) => ({ isNewFromUserWithProfiles }),

    setPrices: (prices) => ({ prices }),
    changePrices: (type, field, value) => ({ type, field, value }),

    setUserProfile: (profile) => ({ profile }),
    openFiltering: () => ({}),
    closeFiltering: () => ({}),

    filter: () => ({}),
    order: (order) => ({ order }),

    fetchUserProfile: (id, name) => ({ id, name }),
    setFetchError: (message) => ({ message }),
    reset: () => ({}),

    getCurrentUser: () => ({}),

    setModalTriggerHeight: (height) => ({ height }),
  }),

  reducers: ({ actions }) => ({
    simulation: [
      {},
      PropTypes.any,
      {
        [actions.setSimulationResults]: (_, payload) => ({ ...payload }),
        [actions.reset]: () => ({}),
      },
    ],
    modalTriggerHeight: [
      null,
      PropTypes.any,
      {
        [actions.setModalTriggerHeight]: (_, p) => p.height,
      },
    ],
    ordering: [
      'annual_billing',
      PropTypes.any,
      {
        [actions.order]: (_, p) => p.order,
      },
    ],
    sugestions: [
      {},
      PropTypes.any,
      {
        [actions.setSimulationSugestions]: (_, payload) => ({ ...payload }),
        [actions.reset]: () => ({}),
      },
    ],

    currentPage: [
      1,
      PropTypes.any,
      {
        [actions.makeSimulation]: () => 1,
        [actions.changePage]: (_, payload) => payload.params.currentPage,
      },
    ],

    simulating: [
      true,
      PropTypes.bool,
      {
        [actions.setSimulationResults]: () => false,
        [actions.makeSimulation]: () => true,
        [actions.setError]: () => false,
      },
    ],

    simulatingPatch: [
      false,
      PropTypes.bool,
      {
        [actions.setSimulationResults]: () => false,
        [actions.setError]: () => false,
      },
    ],

    loadingSugested: [
      true,
      PropTypes.bool,
      {
        [actions.makeSimulation]: () => true,
        [actions.setSimulationSugestions]: () => false,
        [actions.setError]: () => false,
        [actions.changePage]: () => true,
        [actions.filter]: () => true,
      },
    ],

    error: [
      false,
      PropTypes.bool,
      {
        [actions.setError]: (_, payload) => ({ ...payload.error }),
        [actions.makeSimulation]: () => false,
      },
    ],

    filtering: [
      false,
      PropTypes.bool,
      {
        [actions.openFiltering]: () => true,
        [actions.closeFiltering]: () => false,
      },
    ],

    userProfile: [
      {},
      PropTypes.any,
      {
        [actions.setUserProfile]: (state, payload) => payload.profile,
      },
    ],

    pricesForm: [
      {},
      PropTypes.object,
      {
        [actions.setPrices]: (state, payload) => payload.prices,
        [actions.changePrices]: (state, payload) => {
          const type = payload.type
          // If no type: is a boolean toggle of field 'apply_refunds'
          if (!type) {
            return {
              ...state,
              [payload.field]: !state[payload.field],
            }
          } else {
            return {
              ...state,
              [type]: {
                ...state[type],
                [payload.field]: payload.value,
              },
            }
          }
        },
      },
    ],
  }),

  start: function*() {
    const { makeSimulation } = this.actions
    const { profileFinished } = yield DataLogicV2.get('finished')

    // https://sentry2.ubiwhere.com/ubiwhere/simulador-avancado/issues/3364/
    if (!profileFinished) {
      this.props && this.props.router && this.props.router.push('/')
    } else {
      yield put(makeSimulation())
    }
  },

  takeLatest: ({ actions, workers }) => ({
    [actions.makeSimulation]: workers.makeSimulation,
    [actions.setSimulationResults]: workers.fetchSugestions,
    [actions.changePage]: workers.fetchSugestions,
    [actions.filter]: workers.fetchSugestions,
    [actions.order]: workers.fetchSugestions,
    [actions.triggerFilter]: workers.triggerFiltering,
    [actions.resetFilters]: workers.triggerFiltering,
    [actions.saveProfile]: workers.saveProfile,
    [actions.getCurrentUser]: workers.getCurrentUser,
    [actions.getCurrentUser]: workers.getCurrentUser,
    [actions.fetchUserProfile]: workers.fetchUserProfile,
  }),

  workers: {
    *makeSimulation() {
      const {
        setSimulationResults,
        setError,
        setFinished,
        setFilter,
        reset,
      } = this.actions
      const source = axios.CancelToken.source() // Create a cancel token

      try {
        yield put(reset())

        let rawValues = yield DataLogicV2.get('values')
        let values = Object.assign({}, rawValues)

        // Convert to undescore and omit uneeded values
        values = csObjectToUnderscore(values)
        const response = yield call(axios.post, SIMULATOR_ENPOINT, {
          ...values,
          cancelToken: source.token,
        })

        const { data } = response
        yield put(
          setFinished({ simulationFinished: true, simulationError: false })
        )
        yield put(setFilter({ code: data.code }))
        yield put(setSimulationResults(csObjectFromUnderscore(data)))
        const urlParams = new URLSearchParams(window.location.search)
        urlParams.set('code', data.code)
        const url = urlParams.toString()
        window.history.pushState('', '', '?' + url + window.location.hash)

        // If is from a profile already loaded
        // Save the profile and open the modal
        yield delay(800)
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'optimize.activate.results' })
        }
      } catch (error) {
        let simulationErrorCode = 'error'

        if (error.response.data.simulation_code) {
          let rawValues = yield DataLogicV2.get('values')
          let values = Object.assign({}, rawValues)

          if (
            error.response.data.simulation_code[0] ===
            `Object with code=${values.simulationCode} does not exist.`
          ) {
            const getUrl = SIMULATOR_GET_SIMULATION.replace(
              ':code',
              values.simulationCode
            )

            const response = yield call(axios.get, getUrl)

            const { data } = response
            yield put(
              setFinished({ simulationFinished: true, simulationError: false })
            )
            yield put(setFilter({ code: data.code }))
            yield put(setSimulationResults(csObjectFromUnderscore(data)))
            const urlParams = new URLSearchParams(window.location.search)
            urlParams.set('code', data.code)
            const url = urlParams.toString()
            window.history.pushState('', '', '?' + url + window.location.hash)

            // If is from a profile already loaded
            // Save the profile and open the modal
            yield delay(800)
            if (window.dataLayer) {
              window.dataLayer.push({ event: 'optimize.activate.results' })
            }
          }
        }

        if (typeof error.response.data.indexOf === 'function') {
          if (
            error.response.data.indexOf('Electric Price List is Empty') !== -1
          ) {
            simulationErrorCode = 'no_items'
          } else if (
            error.response.data.indexOf('Gas Price List is Empty') !== -1
          ) {
            simulationErrorCode = 'no_items'
          } else if (
            error.response.data.indexOf(
              'Electronic Consumption Registration : Date Colisions, the start_date of an entry must be higher or equal to the previous end_date'
            ) !== -1
          ) {
            simulationErrorCode = 'electric_registration'
          } else if (
            error.response.data.indexOf(
              'Gas Consumption Registration : Date Colisions, the start_date of an entry must be higher or equal to the previous end_date'
            ) !== -1
          ) {
            simulationErrorCode = 'gas_registration'
          } else if (error.response.status === 504) {
            simulationErrorCode = 'timeout'
          }
        } else if (error.response.status === 504) {
          simulationErrorCode = 'timeout'
        }

        yield put(
          setFinished({
            simulationFinished: true,
            simulationError: true,
            simulationErrorCode,
          })
        )
        yield put(setError(error))
      } finally {
        if (yield cancelled()) {
          source.cancel('Saga was cancelled.')
        }
      }
    },

    *fetchSugestions(action) {
      const { setSimulationSugestions, setError, setCompareList } = this.actions
      const { results } = yield this.get('simulation')
      const code = action.payload.results
        ? action.payload.results.code
        : results.code

      const ordering = yield this.get('ordering')
      const filters = yield FiltersLogicV2.get('filters')
      const entitiesOptions = yield FiltersLogicV2.get('entitiesOptions')
      const values = yield DataLogicV2.get('values')
      const actionsParams = action.payload.params ? action.payload.params : {}

      let params = {
        limit: 10,
        ordering,
        is_active: true,
        ...actionsParams,
      }

      Object.entries(filters).forEach(([key, value]) => {
        if (
          value &&
          key !== 'any_billing' &&
          key !== 'any_payment' &&
          key !== 'price_list_time_cycle' &&
          key !== 'entity_designation'
        ) {
          return (params[key] = value)
        } else if (key === 'price_list_time_cycle' && value !== 'all') {
          return (params[key] = value)
        } else if (key === 'entity_designation' && value.length > 0) {
          let newEntityDesignation = ''

          entitiesOptions.forEach((item) => {
            if (filters.entity_designation.includes(item.id)) {
              if (!newEntityDesignation) {
                newEntityDesignation = item.label
              } else {
                newEntityDesignation = `${newEntityDesignation},${item.label}`
              }
            }
          })

          return (params.entity_designation = newEntityDesignation)
        }
      })

      if (params.contract_duration) {
        params.no_contract_duration = !params.contract_duration
        delete params.contract_duration
      }

      let url = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'entity_designation') {
          url.set(key, value)
        } else if (value && key === 'entity_designation') {
          url.set(key, value.join(','))
        }
      })

      url.set('offer_type', values.offerType)
      url.set('potencia_contratada', values.potencia_contratada)
      url.set('ciclo_horario', values.ciclo_horario)
      url.set('code', code)
      if (values.gas_stage) {
        url.set('price_step', values.gas_stage)
        url.set('municipality', values.municipality)
      }

      try {
        const response = yield call(axios.get, SIMULATOR_SUGESTIONS_ENDPOINT, {
          params: { code, ...params },
        })
        const { data } = response

        data.results = data.results.map((item) => csObjectFromUnderscore(item))
        data.results = data.results.map((item) => {
          let t = csObjectFromUnderscore(
            item.electricityPriceList || item.gasPriceList
          )
          url.set('id', t.id)

          return {
            ...item,
            url: `${PAGE_URL}/tarifarios/?${url.toString()}`,
          }
        })

        yield put(setSimulationSugestions(data))
        if (action.type !== 'change page (resultsV2)') {
          yield put(setCompareList([]))
        }
        yield delay(600)
        if (window.dataLayer) {
          window.dataLayer.push({ event: 'optimize.activate.results' })
        }
      } catch (error) {
        yield put(setError(error))
        yield put(setSimulationSugestions([]))
      }
    },

    /**
     * Triggers fetching when filtering
     * @param {Object} action Caller action
     */
    *triggerFiltering(action) {
      const { filter } = this.actions
      yield delay(100)
      yield put(filter())
    },

    /**
     * Saves the simulation profile to the user account
     * Flow: https://projects.invisionapp.com/d/main#/console/11390927/270514272/preview
     */
    *saveProfile(action) {
      const { setFetchError } = this.actions
      const userLoggedIn = yield DataLogicV2.get('userLoggedIn')
      const simulation = yield this.get('simulation')
      const isNewFromUserWithProfiles = action.payload.isNewFromUserWithProfiles
      let modalDetails = {
        loggedIn: !!userLoggedIn,
        opened: true,
      }

      try {
        if (userLoggedIn) {
          const requestProfiles = yield call(
            axios.get,
            SIMULATION_PROFILE_ENDPOINT
          )
          const profiles = requestProfiles.data.results
          const hasProfiles = !!profiles.length

          modalDetails.profiles = profiles
          modalDetails.hasProfiles = hasProfiles

          // If user does not have profiles do save
          if (!hasProfiles || isNewFromUserWithProfiles) {
            const nextHouseNumber = profiles.length + 1
            const saveData = {
              name: `${isEnterprise ? 'Empresa' : 'Casa'} ${nextHouseNumber}`,
              simulation_history: simulation.results.code,
            }
            const saveRequest = yield call(
              axios.post,
              SIMULATION_PROFILE_ENDPOINT,
              saveData
            )
            const saved = saveRequest.data

            modalDetails.saved = saved
            modalDetails.type = 'saved'
          } else {
            modalDetails.type = 'update'
          }
        } else {
          modalDetails.type = 'notlogged'
        }
      } catch (error) {
        // Do something here if any of the stuff above fails
        yield put(setFetchError(error))
      }
    },
    /**
     * If is simulation on user profile, get user house
     */
    *fetchUserProfile(action) {
      const { setUserProfile } = this.actions
      const { id } = action.payload
      const url = `${SIMULATION_PROFILE_ENDPOINT}${id}/`

      try {
        const request = yield call(axios.get, url)
        yield put(setUserProfile(request.data))
      } catch (error) {
        console.log(error)
      }
    },
    /**
     * Gets current user logged in
     */
    *getCurrentUser() {
      const { setIsUserLoggedIn, saveProfile } = this.actions

      try {
        const token = window.localStorage.getItem(AUTH_TOKEN_KEY)

        if (!token) {
          yield put(setIsUserLoggedIn(false))
          return
        }

        const me = yield call(axios.get, ME_ENDPOINT, {
          headers: {
            Authorization: `JWT ${token}`,
          },
        })
        yield put(setIsUserLoggedIn(me.data))
        axios.defaults.headers.common['Authorization'] = `JWT ${token}`
        yield put(saveProfile())
      } catch (error) {
        yield put(setIsUserLoggedIn(false))
        window.localStorage.removeItem(AUTH_TOKEN_KEY)
        delete axios.defaults.headers.common['Authorization']
      }
    },
  },
})



// WEBPACK FOOTER //
// ./src/containers/ResultsV2/logic.js